import { Injectable } from '@angular/core';
import {
  RegisterMemberByIdDto,
  RegisterMemberDashboardStat,
  RegisterMemberMarketingDashboardDto,
  RegisterMemberSummaryDto,
  RegisterMemberTableDto,
  RegisterMemberTableRow,
  RegisterMemberTableRowDto,
  RegisterMemberVehicleOptionDto,
} from '../../models/cityparking/register-member-list.model';

@Injectable({
  providedIn: 'root',
})
export class MapperRegisterMemberListService {
  mapTableFromDto(dto: RegisterMemberTableDto): {
    items: RegisterMemberTableRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as RegisterMemberTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as RegisterMemberTableRowDto[];
    const total = dto.total ?? (raw['Total'] as number) ?? 0;
    const pageSize = dto.pageSize ?? (raw['PageSize'] as number) ?? 20;

    return {
      items: rows.map((row) => this.normalizeRow(row)),
      total,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? Math.ceil(total / pageSize),
    };
  }

  mapDashboardStats(dto: RegisterMemberMarketingDashboardDto): RegisterMemberDashboardStat[] {
    const raw = dto as RegisterMemberMarketingDashboardDto & Record<string, unknown>;
    const summary = this.normalizeSummary(
      (dto.summary ?? raw['Summary']) as RegisterMemberSummaryDto | Record<string, unknown>
    );

    return [
      {
        title: 'รายการทั้งหมด',
        value: (summary.totalCount || 0).toLocaleString(),
        icon: 'folder',
        color: 'text-blue-600',
      },
      {
        title: 'ยังไม่ชำระ',
        value: (summary.unpaidCount || 0).toLocaleString(),
        icon: 'pending',
        color: 'text-amber-500',
      },
      {
        title: 'ชำระแล้ว',
        value: (summary.paidCount || 0).toLocaleString(),
        icon: 'check_circle',
        color: 'text-emerald-500',
      },
      {
        title: 'ยกเลิก',
        value: (summary.cancelledCount || 0).toLocaleString(),
        icon: 'cancel',
        color: 'text-rose-500',
      },
    ];
  }

  mapSearchField(type: 'memberName' | 'license' | 'phone' | 'requestNo'): string {
    switch (type) {
      case 'license':
        return 'license';
      case 'phone':
        return 'phone';
      case 'requestNo':
        return 'requestno';
      default:
        return 'membername';
    }
  }

  mapStatusFilter(filterStatus: number): string | undefined {
    switch (filterStatus) {
      case 1:
        return 'unpaid';
      case 2:
        return 'paid';
      case 3:
        return 'cancelled';
      case 4:
        return 'expired';
      default:
        return undefined;
    }
  }

  normalizeDetail(dto: RegisterMemberByIdDto): RegisterMemberByIdDto {
    const raw = dto as RegisterMemberByIdDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      requestNo: dto.requestNo ?? (raw['RequestNo'] as string) ?? '',
      memberName: dto.memberName ?? (raw['MemberName'] as string) ?? '',
      phone: dto.phone ?? (raw['Phone'] as string) ?? '',
      license: dto.license ?? (raw['License'] as string) ?? '',
      province: dto.province ?? (raw['Province'] as string) ?? '',
      feeId: dto.feeId ?? (raw['FeeId'] as number) ?? 0,
      feeName: dto.feeName ?? (raw['FeeName'] as string) ?? '',
      fee: dto.fee ?? (raw['Fee'] as number) ?? 0,
      subscriptionId: dto.subscriptionId ?? (raw['SubscriptionId'] as number) ?? 0,
      status: dto.status ?? (raw['Status'] as number) ?? 0,
      displayStatus: dto.displayStatus ?? (raw['DisplayStatus'] as number) ?? 0,
      canEdit: dto.canEdit ?? (raw['CanEdit'] as boolean) ?? false,
      expireDate: (dto.expireDate ?? raw['ExpireDate'] ?? null) as string | null,
      memberStart: (dto.memberStart ?? raw['MemberStart'] ?? null) as string | null,
      memberEnd: (dto.memberEnd ?? raw['MemberEnd'] ?? null) as string | null,
      paymentDate: (dto.paymentDate ?? raw['PaymentDate'] ?? null) as string | null,
      createDate: (dto.createDate ?? raw['CreateDate'] ?? null) as string | null,
      remarks: dto.remarks ?? (raw['Remarks'] as string) ?? '',
      siteId: dto.siteId ?? (raw['SiteId'] as number) ?? 0,
      cpUserId: dto.cpUserId ?? (raw['CpUserId'] as number) ?? 0,
      cpVehicleId: dto.cpVehicleId ?? (raw['CpVehicleId'] as number) ?? 0,
      registerGroupId: dto.registerGroupId ?? (raw['RegisterGroupId'] as number) ?? 0,
    };
  }

  normalizeVehicleOption(dto: RegisterMemberVehicleOptionDto): {
    vehicleId: number;
    plateNumber: string;
    provinceName: string;
    vehicleTypeName: string;
    label: string;
  } {
    const raw = dto as RegisterMemberVehicleOptionDto & Record<string, unknown>;
    const plateNumber = dto.plateNumber ?? (raw['PlateNumber'] as string) ?? '';
    const provinceName = dto.provinceName ?? (raw['ProvinceName'] as string) ?? '';
    const vehicleTypeName = dto.vehicleTypeName ?? (raw['VehicleTypeName'] as string) ?? '';
    return {
      vehicleId: dto.vehicleId ?? (raw['VehicleId'] as number) ?? 0,
      plateNumber,
      provinceName,
      vehicleTypeName,
      label: [plateNumber, provinceName, vehicleTypeName].filter(Boolean).join(' · '),
    };
  }

  getDisplayStatusLabel(displayStatus: number): string {
    switch (displayStatus) {
      case 1:
        return 'ชำระแล้ว';
      case 2:
        return 'ยกเลิก';
      case 3:
        return 'หมดอายุแล้ว';
      default:
        return 'ยังไม่ชำระ';
    }
  }

  getDisplayStatusClass(displayStatus: number): string {
    switch (displayStatus) {
      case 1:
        return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case 2:
        return 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800';
      case 3:
        return 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700';
      default:
        return 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    }
  }

  getDisplayStatusDotClass(displayStatus: number): string {
    switch (displayStatus) {
      case 1:
        return 'bg-green-500';
      case 2:
        return 'bg-rose-500';
      case 3:
        return 'bg-slate-400';
      default:
        return 'bg-amber-500';
    }
  }

  private normalizeRow(item: RegisterMemberTableRowDto): RegisterMemberTableRow {
    const raw = item as RegisterMemberTableRowDto & Record<string, unknown>;
    return {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      requestNo: item.requestNo ?? (raw['RequestNo'] as string) ?? '',
      memberName: item.memberName ?? (raw['MemberName'] as string) ?? '',
      phone: item.phone ?? (raw['Phone'] as string) ?? '',
      license: item.license ?? (raw['License'] as string) ?? '',
      province: item.province ?? (raw['Province'] as string) ?? '',
      feeName: item.feeName ?? (raw['FeeName'] as string) ?? '',
      fee: item.fee ?? (raw['Fee'] as number) ?? 0,
      status: item.status ?? (raw['Status'] as number) ?? 0,
      displayStatus: item.displayStatus ?? (raw['DisplayStatus'] as number) ?? 0,
      expireDate: (item.expireDate ?? raw['ExpireDate'] ?? null) as string | null,
      memberStart: (item.memberStart ?? raw['MemberStart'] ?? null) as string | null,
      memberEnd: (item.memberEnd ?? raw['MemberEnd'] ?? null) as string | null,
      createDate: (item.createDate ?? raw['CreateDate'] ?? null) as string | null,
      cpUserId: item.cpUserId ?? (raw['CpUserId'] as number) ?? 0,
      cpVehicleId: item.cpVehicleId ?? (raw['CpVehicleId'] as number) ?? 0,
    };
  }

  private normalizeSummary(raw: RegisterMemberSummaryDto | Record<string, unknown>): RegisterMemberSummaryDto {
    const s = raw as RegisterMemberSummaryDto & Record<string, unknown>;
    return {
      totalCount: s.totalCount ?? (s['TotalCount'] as number) ?? 0,
      unpaidCount: s.unpaidCount ?? (s['UnpaidCount'] as number) ?? 0,
      paidCount: s.paidCount ?? (s['PaidCount'] as number) ?? 0,
      cancelledCount: s.cancelledCount ?? (s['CancelledCount'] as number) ?? 0,
      expiredUnpaidCount: s.expiredUnpaidCount ?? (s['ExpiredUnpaidCount'] as number) ?? 0,
      conversionPercent: s.conversionPercent ?? (s['ConversionPercent'] as number) ?? 0,
    };
  }
}
